import React, {useId} from 'react'

/*
 * Achievement badge artwork.
 *
 * Simplified stand-ins for the icons in docs/Exam_Prep_Achievement_Badges.docx: one shared
 * medal frame (gradient ring + inner disc) with a per-badge glyph, so all 25 read as a
 * single family at dashboard size. Inline SVG only -- no new dependency, no image assets,
 * no storage keys.
 *
 * Swapping in final artwork later means replacing the entries in GLYPHS; nothing else in the
 * feature knows what a badge looks like.
 *
 * Coordinate space is a 48x48 viewBox. Glyphs live inside the inner disc, roughly the
 * 12..36 box on both axes.
 */

// ring colours per category, from the design tokens' accent families
const CATEGORY_ACCENT = {
	// orange, deliberately clear of the yellow-amber Daily Streaks use (167)
	'Onboarding':         ['#FB923C', '#EA580C'],
	'Volume Milestones':  ['#818CF8', '#4F46E5'],
	'Topic Mastery':      ['#34D399', '#059669'],
	'Daily Streaks':      ['#FBBF24', '#D97706'],
	'Weekly Consistency': ['#22D3EE', '#0891B2'],
	'Performance':        ['#F472B6', '#DB2777'],
	'Practice Exams':     ['#60A5FA', '#2563EB'],
	'Engagement':         ['#A78BFA', '#7C3AED'],
}

const LOCKED_ACCENT = ['#9CA3AF', '#6B7280']

/* ---------- glyph building blocks ---------- */

// numerals sit better than a pictogram for the counting badges
const Numeral = ({children, size = 13, y = 25}) => (
	<text
		x="24" y={y}
		textAnchor="middle" dominantBaseline="central"
		fontSize={size} fontWeight="800" fontFamily="system-ui, sans-serif"
		fill="currentColor" stroke="none"
	>
		{children}
	</text>
)

/*
 * Streak badges are a flame over the day count. The count sits below the flame rather than
 * inside it: inside the outline there is only ~6px of clear space, which made two digits
 * unreadable at dashboard size.
 */
const FlameCount = ({children}) => (
	<>
		<path d="M24 11c3.6 3.9 5.8 6.1 5.8 9.2A5.8 5.8 0 0 1 24 26a5.8 5.8 0 0 1-5.8-5.8C18.2 17.1 20.4 14.9 24 11z"/>
		<Numeral size={11} y={33.5}>{children}</Numeral>
	</>
)

const Calendar = () => (
	<>
		<rect x="14" y="16" width="20" height="18" rx="2.5"/>
		<path d="M14 22h20M19 13v4M29 13v4"/>
	</>
)

const Clipboard = () => (
	<>
		<rect x="15" y="14" width="18" height="21" rx="2.5"/>
		<path d="M20 14v-2h8v2"/>
	</>
)

/*
 * token -> glyph. Every glyph is stroked in currentColor by the wrapper, so a glyph only
 * describes shape.
 */
const GLYPHS = {
	/* ---- Onboarding ---- */
	'welcome-aboard': (
		// a pennant on a pole: planted your flag, nothing measured yet
		<>
			<path d="M18 35V13"/>
			<path d="M18 14h14l-3.5 5 3.5 5H18"/>
		</>
	),

	/* ---- Volume Milestones: footprints, then the count ---- */
	'first-steps': (
		// two footprints walking up-right: sole plus a row of toe dots
		<>
			<g transform="rotate(-18 19 22)">
				<ellipse cx="19" cy="23" rx="3.2" ry="4.6"/>
				<path d="M16.4 17.4v.01M19 16.8v.01M21.6 17.4v.01" strokeWidth="2.2"/>
			</g>
			<g transform="rotate(-18 29 29)">
				<ellipse cx="29" cy="30" rx="3.2" ry="4.6"/>
				<path d="M26.4 24.4v.01M29 23.8v.01M31.6 24.4v.01" strokeWidth="2.2"/>
			</g>
		</>
	),
	'century-club':     <Numeral size={12}>100</Numeral>,
	'club-500':         <Numeral size={12}>500</Numeral>,
	'club-1000':        <Numeral size={13}>1K</Numeral>,
	'question-machine': (
		<>
			<circle cx="24" cy="24" r="8.5"/>
			<path d="M21.5 21.5a2.5 2.5 0 1 1 3.4 2.3c-.6.3-.9.9-.9 1.6M24 28.5v.01"/>
			<path d="M24 12v2.5M24 33.5V36M12 24h2.5M33.5 24H36"/>
		</>
	),

	/* ---- Topic Mastery ---- */
	'topic-finisher': (
		<>
			<path d="M15 18h9M15 24h9M15 30h5"/>
			<path d="M26 29l3 3 6-7"/>
		</>
	),
	'subject-master': (
		<>
			<path d="M14 30h20M14 30v-3h20v3"/>
			<path d="M16 27v-4h16v4M18 23v-4h12v4"/>
			<path d="M22 19l2 2 2-2"/>
		</>
	),
	'course-completionist': (
		<>
			<path d="M18 14h12v6a6 6 0 0 1-12 0v-6z"/>
			<path d="M18 16h-3v2a3 3 0 0 0 3 3M30 16h3v2a3 3 0 0 1-3 3"/>
			<path d="M24 26v5M19 34h10"/>
		</>
	),

	/* ---- Daily Streaks: a flame plus the day count ---- */
	'getting-started':   <FlameCount>2</FlameCount>,
	'on-a-roll':         <FlameCount>4</FlameCount>,
	'week-warrior':      <FlameCount>7</FlameCount>,
	'unstoppable':       <FlameCount>14</FlameCount>,
	'thirty-day-legend': <FlameCount>30</FlameCount>,

	/* ---- Weekly Consistency ---- */
	'consistent-practicer': (
		<>
			<Calendar/>
			<path d="M19 27v.01M23 27v.01M27 27v.01M31 27v.01" strokeWidth="2.75"/>
		</>
	),
	'habit-builder':    <><Calendar/><Numeral size={9} y={29}>2</Numeral></>,
	'dedicated-learner':<><Calendar/><Numeral size={9} y={29}>3</Numeral></>,
	'monthly-momentum': (
		<>
			<Calendar/>
			<path d="M20 30l4-4 4 4M24 26v6"/>
		</>
	),

	/* ---- Performance ---- */
	'sharp-shooter': (
		<>
			<circle cx="24" cy="24" r="9"/>
			<circle cx="24" cy="24" r="4.5"/>
			<path d="M24 24l9-9M30 15h3v3" strokeWidth="2"/>
		</>
	),
	'perfect-round': (
		<>
			<path d="M24 13l3.4 6.9 7.6 1.1-5.5 5.4 1.3 7.6L24 30.4l-6.8 3.6 1.3-7.6-5.5-5.4 7.6-1.1L24 13z"/>
		</>
	),
	'most-improved': (
		<>
			<path d="M14 32l6-6 4 3 8-9"/>
			<path d="M28 20h6v6"/>
			<path d="M13 35h22" opacity="0.45"/>
		</>
	),

	/* ---- Practice Exams ---- */
	'full-length-finisher': (
		<>
			<Clipboard/>
			<circle cx="24" cy="25" r="6"/>
			<path d="M24 22v3.2l2.2 1.6"/>
		</>
	),
	'exam-ready': (
		<>
			<Clipboard/>
			<path d="M19 21h10M19 25.5h10"/>
			<path d="M19.5 30l2.5 2.5 5-5.5"/>
		</>
	),

	/* ---- Engagement ---- */
	'welcome-back': (
		<>
			<path d="M33 24a9 9 0 1 1-3.2-6.9"/>
			<path d="M33 14v4.5h-4.5"/>
			<path d="M24 20v4.5l3 2"/>
		</>
	),
	'early-bird': (
		<>
			<path d="M24 27a5.5 5.5 0 0 1 11 0" transform="translate(-5.5 0)"/>
			<path d="M14 30h20"/>
			<path d="M24 14v3M15.5 17.5l2 2M32.5 17.5l-2 2M12.5 26h2.5M33 26h2.5"/>
		</>
	),
	'night-owl': (
		<>
			<path d="M29.5 15a9 9 0 1 0 4.5 12 10 10 0 0 1-4.5-12z"/>
			<path d="M17 17v.01M15 22v.01M20 14v.01" strokeWidth="2.5"/>
		</>
	),
}

/*
 * @param token       catalog token, e.g. 'week-warrior'
 * @param category    catalog category, drives the ring colour
 * @param size        rendered px (square)
 * @param earned      false renders the locked/greyed treatment
 */
const AchievementBadgeIcon = ({token, category, size = 56, earned = true, title}) => {
	// unique per instance: two badges on screen must not share gradient ids
	const uid = useId().replace(/:/g, '')
	const ringId = `badge-ring-${uid}`

	const [light, dark] = earned
		? (CATEGORY_ACCENT[category] || CATEGORY_ACCENT['Volume Milestones'])
		: LOCKED_ACCENT

	const glyph = GLYPHS[token] || GLYPHS['perfect-round']

	return (
		<svg
			width={size} height={size} viewBox="0 0 48 48"
			role="img" aria-label={title || token}
			style={{display: 'block', opacity: earned ? 1 : 0.45, flexShrink: 0}}
		>
			{title && <title>{title}</title>}
			<defs>
				<linearGradient id={ringId} x1="0" y1="0" x2="1" y2="1">
					<stop offset="0%" stopColor={light}/>
					<stop offset="100%" stopColor={dark}/>
				</linearGradient>
			</defs>

			{/* medal ring */}
			<circle cx="24" cy="24" r="21" fill={`url(#${ringId})`}/>
			{/* inner disc: the glyph sits on this, tinted by the ring colour */}
			<circle cx="24" cy="24" r="16.5" fill={dark} fillOpacity="0.22"/>
			<circle cx="24" cy="24" r="16.5" fill="#0F101A" fillOpacity="0.55"/>

			<g
				color={light}
				stroke="currentColor"
				strokeWidth="1.9"
				strokeLinecap="round"
				strokeLinejoin="round"
				fill="none"
			>
				{glyph}
			</g>
		</svg>
	)
}

export {CATEGORY_ACCENT}
export default AchievementBadgeIcon
